/**
 * Host Global - Property Owner Reservations Calendar
 * Read-only calendar view of all bookings for owner's properties
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

// react-big-calendar
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";

// API
import { propertyOwnerAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

const localizer = momentLocalizer(moment);

function ReservationsCalendar() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Fetch reservations
  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const response = await propertyOwnerAPI.getReservations();
      setReservations(response.data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load reservations. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Convert reservations to calendar events
  const calendarEvents = useMemo(() => {
    return reservations.map((reservation) => {
      // Determine color based on status
      let color = "#1976d2"; // Default blue for booked
      if (reservation.status === "checked_in") color = "#4caf50"; // Green
      else if (reservation.status === "checked_out") color = "#9e9e9e"; // Gray
      else if (reservation.status === "cancelled") color = "#f44336"; // Red

      return {
        id: reservation.reservation_id,
        title: `${reservation.property_name} - ${reservation.reservation_number}`,
        start: new Date(reservation.start_date),
        end: new Date(reservation.end_date),
        resource: reservation,
        color: color,
      };
    });
  }, [reservations]);

  // Event style getter
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color || "#1976d2",
        borderRadius: "4px",
        opacity: 0.9,
        color: "white",
        border: "none",
        display: "block",
        padding: "4px 8px",
        fontSize: "0.875rem",
        fontWeight: 500,
      },
    };
  };

  // Handle event click
  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setDialogOpen(true);
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Handle navigation
  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <PropertyOwnerFooter />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={4}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/property-owner/my-properties")}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{ mb: 2 }}
          >
            Back to Properties
          </MDButton>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            Reservations Calendar
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            View all bookings for your properties
          </MDTypography>
        </MDBox>

        {/* Message Alert */}
        {message.text && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Calendar Controls */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, mb: 3 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" gap={1}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() =>
                      handleNavigate(
                        moment(date)
                          .subtract(
                            1,
                            view === "month" ? "month" : view === "week" ? "week" : "day"
                          )
                          .toDate()
                      )
                    }
                    startIcon={<Icon>chevron_left</Icon>}
                  >
                    Previous
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => handleNavigate(new Date())}
                  >
                    Today
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() =>
                      handleNavigate(
                        moment(date)
                          .add(1, view === "month" ? "month" : view === "week" ? "week" : "day")
                          .toDate()
                      )
                    }
                    endIcon={<Icon>chevron_right</Icon>}
                  >
                    Next
                  </MDButton>
                </MDBox>
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    variant={view === "month" ? "contained" : "outlined"}
                    onClick={() => handleViewChange("month")}
                  >
                    Month
                  </Button>
                  <Button
                    variant={view === "week" ? "contained" : "outlined"}
                    onClick={() => handleViewChange("week")}
                  >
                    Week
                  </Button>
                  <Button
                    variant={view === "day" ? "contained" : "outlined"}
                    onClick={() => handleViewChange("day")}
                  >
                    Day
                  </Button>
                </ButtonGroup>
              </MDBox>
            </Card>
          </Grid>

          {/* Calendar */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              {reservations.length === 0 ? (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={8}
                  sx={{ minHeight: "500px" }}
                >
                  <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>event_busy</Icon>
                  <MDTypography variant="h6" color="text.secondary" mb={1}>
                    No Reservations Found
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    You don't have any bookings yet. Reservations will appear here once clients book
                    your properties.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox
                  sx={{
                    height: view === "month" ? "600px" : view === "week" ? "500px" : "400px",
                    "& .rbc-calendar": {
                      fontFamily: "inherit",
                    },
                    "& .rbc-header": {
                      padding: "12px 8px",
                      fontWeight: 600,
                      borderBottom: "2px solid #e0e0e0",
                    },
                    "& .rbc-today": {
                      backgroundColor: "rgba(25, 118, 210, 0.1)",
                    },
                    "& .rbc-off-range-bg": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .rbc-event": {
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    },
                    "& .rbc-event:hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: "100%" }}
                    view={view}
                    date={date}
                    onView={handleViewChange}
                    onNavigate={handleNavigate}
                    onSelectEvent={handleSelectEvent}
                    eventPropGetter={eventStyleGetter}
                    popup
                    showMultiDayTimes
                  />
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* Status Legend */}
          {reservations.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDBox display="flex" gap={3} alignItems="center" flexWrap="wrap">
                  <MDTypography variant="button" fontWeight="medium">
                    Status Legend:
                  </MDTypography>
                  <Chip
                    label="Booked"
                    sx={{ bgcolor: "#1976d2", color: "white", fontWeight: 600 }}
                    size="small"
                  />
                  <Chip
                    label="Checked In"
                    sx={{ bgcolor: "#4caf50", color: "white", fontWeight: 600 }}
                    size="small"
                  />
                  <Chip
                    label="Checked Out"
                    sx={{ bgcolor: "#9e9e9e", color: "white", fontWeight: 600 }}
                    size="small"
                  />
                  <Chip
                    label="Cancelled"
                    sx={{ bgcolor: "#f44336", color: "white", fontWeight: 600 }}
                    size="small"
                  />
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>

      {/* Reservation Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="info">event</Icon>
              Reservation Details
            </MDTypography>
            <MDButton iconOnly variant="text" color="inherit" onClick={() => setDialogOpen(false)}>
              <Icon>close</Icon>
            </MDButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <MDBox>
              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Property Name
                </MDTypography>
                <MDTypography variant="h6" fontWeight="bold">
                  {selectedEvent.property_name}
                </MDTypography>
                {selectedEvent.property_short_id && (
                  <MDTypography variant="caption" color="text.secondary">
                    ID: {selectedEvent.property_short_id}
                  </MDTypography>
                )}
              </MDBox>

              <Divider sx={{ my: 2 }} />

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Reservation Number
                </MDTypography>
                <MDTypography variant="body1" fontWeight="medium">
                  {selectedEvent.reservation_number}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Booking Dates
                </MDTypography>
                <MDTypography variant="body1">
                  {formatDate(selectedEvent.start_date)} - {formatDate(selectedEvent.end_date)}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Status
                </MDTypography>
                <Chip
                  label={selectedEvent.status.replace("_", " ").toUpperCase()}
                  sx={{
                    bgcolor:
                      selectedEvent.status === "booked"
                        ? "#1976d2"
                        : selectedEvent.status === "checked_in"
                        ? "#4caf50"
                        : selectedEvent.status === "checked_out"
                        ? "#9e9e9e"
                        : "#f44336",
                    color: "white",
                    fontWeight: 600,
                  }}
                  size="small"
                />
              </MDBox>

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Total Revenue
                </MDTypography>
                <MDTypography variant="h6" fontWeight="bold" color="success.main">
                  {formatCurrency(selectedEvent.total_amount)}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Insurance Deposit
                </MDTypography>
                <MDTypography variant="body1" fontWeight="medium">
                  {formatCurrency(selectedEvent.insurance_deposit)}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Number of Guests
                </MDTypography>
                <MDTypography variant="body1">{selectedEvent.number_of_guests}</MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Client Name
                </MDTypography>
                <MDTypography variant="body1">{selectedEvent.client_name}</MDTypography>
              </MDBox>

              <MDBox>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  display="block"
                  mb={0.5}
                >
                  Created At
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  {formatDate(selectedEvent.created_at)}
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          {selectedEvent?.reservation_id && (
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => {
                setDialogOpen(false);
                navigate(`/property-owner/reservations/${selectedEvent.reservation_id}`);
              }}
              sx={{ borderRadius: 2, mr: 1 }}
            >
              View Full Details
            </MDButton>
          )}
          <MDButton onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <PropertyOwnerFooter />
    </DashboardLayout>
  );
}

export default ReservationsCalendar;
